import { Box, Card, VStack } from '@chakra-ui/react'
import { createFileRoute } from '@tanstack/react-router'
import mqtt from 'mqtt'
import { useEffect, useRef, useState } from 'react'
import Chat from '~/components/Chat'
import { PageHeader } from '~/components/shell'

export const Route = createFileRoute('/_app/energy/bot')({ component: EnergyUnsBot })

const hivemqws = import.meta.env.VITE_SHARED_WS_ADDRESS

const ENERGY_BOT_TOPIC_FILTERS = ['ecorp/generation/#', 'ecorp/storage/#']

interface TopicNode {
  name: string
  attributes: {
    index: number
    value?: string
  }
  children?: TopicNode[]
}

const topicsRoot: TopicNode = {
  name: 'Root',
  attributes: {
    index: 0,
  },
  children: [],
}

const uns_options: mqtt.IClientOptions = {
  protocol: 'wss',
  port: 443,
  keepalive: 20,
  clientId: `stories_uns_energy_bot_${Math.random().toString(16).substr(2, 8)}`,
  username: import.meta.env.VITE_ENERGY_WS_USERNAME,
  password: import.meta.env.VITE_ENERGY_WS_PASSWORD,
}

let uns_client: mqtt.MqttClient | null = null

function EnergyUnsBot() {
  const promptinit = `ROLE
You are an analytics expert for the ECorp energy Unified Namespace. You analyse live MQTT telemetry from a multi-technology fleet: offshore wind, utility-scale solar PV, hydro, and battery energy storage (BESS).

CONTEXT
Topics flow under ecorp/generation/* and ecorp/storage/*. The dataset is a snapshot of the live MQTT tree. Field reference:
- Wind (ecorp/generation/<site>/wind-offshore/<plant>/wtg-<N>/telemetry): active_power_kw, reactive_power_kvar, wind_speed_ms, wind_dir_deg, rotor_rpm, pitch_deg, nacelle_dir_deg, gearbox_oil_temp_c, vibration_drivetrain_mms, grid_freq_hz, availability_state, curtailment_setpoint_kw
- Solar PV (ecorp/generation/<site>/solar-pv/<plant>/string-<N>/inverter-<M>/telemetry): ac_power_kw, dc_power_kw, dc_voltage_v, dc_current_a, irradiance_wm2, panel_temp_c, ambient_temp_c, performance_ratio_pct, state
- Hydro (ecorp/generation/<site>/hydro/<plant>/unit-<N>/telemetry): active_power_mw, head_m, flow_m3s, turbine_speed_rpm, generator_temp_c, water_level_m, governor_state
- BESS (ecorp/storage/<site>/bess/<site>/rack-<N>/module-<M>/telemetry): soc_pct, soh_pct, voltage_v, current_a, cell_temp_min_c, cell_temp_max_c, cycle_count, alarm_state

ANSWER RULES
- Answer only from the dataset below. Never invent assets, sites, values, or fields that are not present.
- Lead with the result: one short sentence, a small bullet list, or a compact markdown table.
- Follow with at most 2 to 4 sentences of plain-language explanation. Do not exceed that.
- Always include units (kW, MW, m/s, °C, psi, %, m³/s, Hz, rpm, mm/s).
- Round numeric values to two decimals where appropriate.
- For comparisons across sites or technologies, prefer a markdown table to prose.
- If the dataset does not contain enough information to answer, say so in one sentence and stop.
- Never include code, pseudocode, SQL, JSON, schemas, formulas, or step-by-step calculations. The user wants the conclusion, not the method.
- No greetings, no caveats, no mention of being an AI, no emojis.

DATASET (live MQTT topic tree, JSON):
`
  const introMessage =
    "Hi, I'm EnerBot. Ask me anything about wind, solar, hydro, or BESS telemetry in the ECorp UNS."
  const questionList = [
    'What is the total active power being generated right now across wind, solar, and hydro? Break it down by technology.',
    'Which wind turbine has the highest gearbox oil temperature, and is it close to alarm levels?',
    'Compare the average active power output of wind, solar, and hydro by site.',
    'Are any BESS modules reporting an alarm_state other than OK? List the rack and module IDs.',
    'What is the average state of charge (SoC) across all BESS racks? Which site has the lowest SoC?',
    'Which solar PV string has the highest performance ratio, and which has the lowest? What might explain the gap?',
    'How does wind speed correlate with active power on the wind fleet right now? Are any turbines curtailed?',
    'What is the highest cell temperature recorded across all BESS modules, and on which rack?',
    'Which hydro unit has the highest active power, and what are its head and flow values?',
  ]

  const [topics, setTopics] = useState<TopicNode>(topicsRoot)
  const [isConnected, setIsConnected] = useState(false)
  const [messagesPerSecond, setMessagesPerSecond] = useState(0)
  const messageCountRef = useRef(0)
  const lastCountRef = useRef(0)
  const mpsIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  function connect_mqtt(broker: string) {
    uns_client?.end()
    uns_client = mqtt.connect(`wss://${broker}:443/mqtt`, uns_options)

    uns_client.on('message', (topic, message) => {
      messageCountRef.current++
      updateTreeWithNewNode(topic.toString(), message.toString())
    })

    uns_client.on('connect', () => {
      console.log(`Connected to ${broker}`)
      setIsConnected(true)
      uns_client?.subscribe(ENERGY_BOT_TOPIC_FILTERS, { qos: 1 }, (err) => {
        if (err) {
          console.log(err)
        }
      })
    })

    uns_client.on('close', () => {
      setIsConnected(false)
    })

    uns_client.on('error', (err) => {
      console.log('Error happened')
      console.log(err)
    })
  }

  async function disconnect_mqtt(client: mqtt.MqttClient | null) {
    await client?.end()
    console.log('Disconnected')
  }

  function updateTreeWithNewNode(topic: string, message: string) {
    let parsedValue: unknown
    try {
      const parsed = JSON.parse(message)
      parsedValue = parsed.value ?? parsed
    } catch {
      parsedValue = message
    }

    const segments = topic.split('/')
    const nextData = Object.assign({}, topics)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let currentNode: any = nextData
    segments.forEach((segment: string) => {
      if (!currentNode.children) {
        currentNode.children = []
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let existingNode = currentNode.children.find((n: any) => n.name === segment)
      if (!existingNode) {
        const newNode = {
          name: segment,
          attributes: { index: 1, value: parsedValue },
        }
        currentNode.children.push(newNode)
        existingNode = newNode
      } else {
        existingNode.attributes.index = existingNode.attributes.index + 1
        existingNode.attributes.value = parsedValue
      }
      currentNode = existingNode
    })
    setTopics(nextData)
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: connect/disconnect run once on mount by design, not on every render
  useEffect(() => {
    if (hivemqws) {
      connect_mqtt(hivemqws)
    }
    mpsIntervalRef.current = setInterval(() => {
      const current = messageCountRef.current
      const mps = current - lastCountRef.current
      lastCountRef.current = current
      setMessagesPerSecond(mps)
    }, 1000)
    return () => {
      disconnect_mqtt(uns_client)
      if (mpsIntervalRef.current) {
        clearInterval(mpsIntervalRef.current)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <Box p={6} h="calc(100vh - 120px)">
      <VStack align="stretch" gap={4} h="100%">
        <PageHeader title="Energy - AI Bot" subtitle="Ask AI to extract information from the UNS" />

        <Card.Root flex={1} overflow="hidden" boxShadow="2xl" display="flex" flexDirection="column">
          <Card.Body overflow="auto" flex="1" minH={0}>
            <Box h="100%">
              <Chat
                introMessage={introMessage}
                promptinit={promptinit}
                topics={topics}
                questionList={questionList}
                isConnected={isConnected}
                messagesPerSecond={messagesPerSecond}
              />
            </Box>
          </Card.Body>
        </Card.Root>
      </VStack>
    </Box>
  )
}
